import { getCurrentUser } from 'aws-amplify/auth'
import API from '@/services/API'
import { organisationUserByCognitoIdentityId } from '@/graphql/queries'
import { getFullUserByCognitoId } from '@/graphql/custom/getFullUserByCognitoId'
import { OrganisationUser } from '@/API'

export interface OrganisationUserAccess {
  hasAccess: boolean
  organisationUser?: OrganisationUser
}

export interface OrganisationUserListAccess {
  hasAccess: boolean
  organisationUsers: OrganisationUser[]
}

/**
 * Get the current user's organisation user details
 * @returns Object with hasAccess boolean and optional organisationUser details
 */
export async function getOrganisationUserAccess (): Promise<OrganisationUserAccess> {
  try {
    const authUser = await getCurrentUser()
    if (!authUser?.username) {
      return { hasAccess: false }
    }

    const cognitoIdentityResponse: any = await API.graphql({
      query: getFullUserByCognitoId,
      variables: {
        cognitoId: authUser.username
      }
    })

    const cognitoIdentity = cognitoIdentityResponse.data?.getUserByCognitoId?.items?.[0]
    if (!cognitoIdentity?.id) {
      return { hasAccess: false }
    }

    const response: any = await API.graphql({
      query: organisationUserByCognitoIdentityId,
      variables: {
        cognitoIdentityId: cognitoIdentity.id
      }
    })

    const organisationUser = response.data?.organisationUserByCognitoIdentityId?.items?.[0]
    if (!organisationUser) {
      return { hasAccess: false }
    }

    return {
      hasAccess: true,
      organisationUser
    }
  } catch (error) {
    console.error('Failed to check organisation user access:', error)
    return { hasAccess: false }
  }
}

/**
 * Check if the current user has organisation user access
 * @returns true if user has access, false otherwise
 */
export async function hasOrganisationUserAccess (): Promise<boolean> {
  const { hasAccess } = await getOrganisationUserAccess()
  return hasAccess
}

/**
 * Get all organisation user records for the current user
 * @returns Object with hasAccess boolean and list of organisationUsers
 */
export async function getAllOrganisationUserAccess (): Promise<OrganisationUserListAccess> {
  try {
    const authUser = await getCurrentUser()
    if (!authUser?.username) {
      return { hasAccess: false, organisationUsers: [] }
    }

    const cognitoIdentityResponse: any = await API.graphql({
      query: getFullUserByCognitoId,
      variables: {
        cognitoId: authUser.username
      }
    })

    const cognitoIdentity = cognitoIdentityResponse.data?.getUserByCognitoId?.items?.[0]
    if (!cognitoIdentity?.id) {
      return { hasAccess: false, organisationUsers: [] }
    }

    const organisationUsers: OrganisationUser[] = []
    let nextToken: string | null = null

    do {
      const response: any = await API.graphql({
        query: organisationUserByCognitoIdentityId,
        variables: {
          cognitoIdentityId: cognitoIdentity.id,
          nextToken
        }
      })

      const items = response.data?.organisationUserByCognitoIdentityId?.items ?? []
      organisationUsers.push(...items.filter((item: OrganisationUser | null) => item !== null))
      nextToken = response.data?.organisationUserByCognitoIdentityId?.nextToken ?? null
    } while (nextToken)

    return {
      hasAccess: organisationUsers.length > 0,
      organisationUsers
    }
  } catch (error) {
    console.error('Failed to get all organisation user access:', error)
    return { hasAccess: false, organisationUsers: [] }
  }
}
